import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { style: {"margin":"0px -10px"} }

import { ref, computed, watch, onMounted, nextTick } from 'vue';
import type { PropType } from 'vue';
import type { FormField } from '@/types/form';
import { remoteOptionsRegistry } from '@/lib/remoteOptions';
import debounce from 'lodash.debounce';


export default /*@__PURE__*/_defineComponent({
  __name: 'DynamicJsonField',
  props: {
  modelValue: Array as PropType<any[]>,
  fields: Array as PropType<FormField[]>,
  summary: Object as PropType<{
    show: boolean;
    fields?: string[];
    formatter?: (items: Record<string, any>[]) => Record<string, any>;
  }>,
  form: { type: Object as PropType<Record<string, any>>, required: true }
},
  emits: ['update:modelValue', 'validation'],
  setup(__props, { emit: __emit }) {

const props = __props;
const emit = __emit;

const localValue = ref<any[]>([]);
const rowValidations = ref<boolean[]>([]);

// 🔁 Para evitar loops infinitos al sincronizar
let skipNextSync = false;

watch(
  () => props.modelValue,
  (newVal) => {
    skipNextSync = true;
    if (Array.isArray(newVal) && newVal.length) {
      localValue.value = newVal.map(row => ({ ...row }));
      rowValidations.value = newVal.map(() => true);
    } else {
      localValue.value = [{}];
      rowValidations.value = [false];
    }
    nextTick(() => {
      skipNextSync = false;
    });
  },
  { immediate: true, deep: true }
);

watch(
  () => localValue.value.map(row => ({ ...row })),
  (val) => {
    if (!skipNextSync) {
      emit('update:modelValue', val);
    }
  },
  { deep: true }
);

watch(rowValidations, () => {
  emit('validation', rowValidations.value.every(Boolean));
}, { deep: true });

const rowModels = computed(() =>
  localValue.value.map((row) => ({
    get value() {
      return row;
    },
    set value(val) {
      Object.assign(row, val);
    }
  }))
);

const debouncedFetchers: Record<string, any> = {};

const addRow = async () => {
  const row: Record<string, any> = {};
  props.fields?.forEach(f => (row[f.value] = null));
  localValue.value.push(row);
  rowValidations.value.push(false);
  await nextTick();
  const lastRow = document.querySelectorAll('[data-dynamic-row]')?.[localValue.value.length - 1];
  const input = lastRow?.querySelector('input, textarea, select');
  if (input) (input as HTMLElement).focus();
};

const removeRow = (index: number) => {
  localValue.value.splice(index, 1);
  rowValidations.value.splice(index, 1);
  if (localValue.value.length === 0) addRow();
};

const updateRowValidation = (index: number, valid: boolean) => {
  rowValidations.value[index] = valid;
};

const getProps = (field: FormField, row: any) => {
  const { children, component, value, ...rest } = field;
  const props: Record<string, any> = {
    ...rest,
    label: field.label,
    type: field.type || 'text',
    clearable: false,
    density: 'compact',
    rules: field.required ? [(v: any) => !!v || 'Requerido'] : [],
    'item-title': field.itemText || 'label',
    'item-value': field.itemValue || 'value',
    disabled: field.permission === 'disabled',
    readonly: field.readonly,
    'prepend-inner-icon': field.icon,
  };
  if (field.options) props.items = field.options;
  return props;
};

const rowOptions: Record<string, any[]> = {};
const rowLoading = ref<Record<string, boolean>>({});

function getFetchParamsForRow(field: any, form: any, row: any, search: string) {
  let params: Record<string, any> = {};
  if (field.searchValue) params[`filter[${field.searchValue}]`] = search;
  if (typeof field.fetchParams === 'function') {
    // Pasa el contexto con form + row (clave en #1)
    params = { ...params, ...field.fetchParams({ ...form, ...row }) };
  }
  return params;
}

// 🔥 ¡La clave! Igual que en CreateForm:
const getFieldProps = (field: FormField, row: any, rowIndex: number) => {
  const props: Record<string, any> = {
    ...getProps(field, row),
  };

  // --- FETCH REMOTO POR FILA ---
  if (field.fetchOptions && remoteOptionsRegistry[field.fetchOptions]) {
    const fetchFn = remoteOptionsRegistry[field.fetchOptions as any];
    const key = `${field.value}-${rowIndex}`;

    // asegura estructura por fila
    if (!rowOptions[key]) rowOptions[key] = [];
    if (rowLoading.value[key] === undefined) rowLoading.value[key] = false;

    // Debounced search por fila
    if (!debouncedFetchers[key]) {
      debouncedFetchers[key] = debounce(async (val: string) => {
        rowLoading.value[key] = true;
        try {
          const params = getFetchParamsForRow(field, props.form, row, val || '');
          const results = await fetchFn(params);
          rowOptions[key] = Array.isArray(results) ? [...results] : [];
        } finally {
          rowLoading.value[key] = false;
        }
      }, 500);
    }

    // Prefetch al abrir (como la #2)
    props.onFocus = (open: boolean) => {
      if (open && (!rowOptions[key] || rowOptions[key].length === 0)) {
        rowLoading.value[key] = true;
        const params = getFetchParamsForRow(field, props.form, row, '');
        Promise.resolve(fetchFn(params))
          .then(results => {
            rowOptions[key] = Array.isArray(results) ? [...results] : [];
          })
          .finally(() => {
            rowLoading.value[key] = false;
          });
      }
    };

    // Búsqueda al escribir
    props['onUpdate:search'] = (val: string) => {
      // si quieres purgar con corto input, descomenta:
      // if (typeof val === 'string' && val.trim().length < 1) {
      //   rowOptions[key] = [];
      //   return;
      // }
      debouncedFetchers[key](val || '');
    };

    // Items y loading por fila (NO usar field.options global)
    props.items = rowOptions[key];
    props.loading = !!rowLoading.value[key];

    // Mantén la API que usa tu componente (title/value personalizados)
    props['item-title'] = field.itemText || 'label';
    props['item-value'] = field.itemValue || 'value';
  }

  return props;
};

const getCustomProps = (field: FormField, row: any, index: number) => {
  return typeof field.props === 'function' ? field.props(row, props.form) : {};
};

const formatCurrency = (val: number) => `$${val.toFixed(2)}`;

const getTotal = computed(() => {
  return props.summary?.formatter ? props.summary.formatter(localValue.value).total || 0 : 0;
});

return (_ctx: any,_cache: any) => {
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_divider = _resolveComponent("v-divider")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localValue.value, (row, index) => {
      return (_openBlock(), _createElementBlock("div", { key: index }, [
        _createVNode(_component_v_row, {
          class: "ma-0",
          "data-dynamic-row": true
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_col, {
              cols: "11",
              class: "pa-0"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_row, { class: "ma-0" }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.fields, (field) => {
                      return (_openBlock(), _createBlock(_component_v_col, {
                        key: field.value,
                        cols: field.col || 12,
                        class: "pa-0 pr-2"
                      }, {
                        default: _withCtx(() => [
                          (field.isCustom)
                            ? (_openBlock(), _createBlock(_resolveDynamicComponent(field.component), _mergeProps({
                                key: `custom-${field.value}-${index}`,
                                modelValue: rowModels.value[index].value,
                                "onUpdate:modelValue": ($event: any) => ((rowModels.value[index].value) = $event)
                              }, { ref_for: true }, getCustomProps(field, row, index), {
                                onValidation: (valid) => updateRowValidation(index, valid),
                                variant: "solo-filled"
                              }), null, 16, ["modelValue", "onUpdate:modelValue", "onValidation"]))
                            : (_openBlock(), _createBlock(_resolveDynamicComponent(field.component), _mergeProps({
                                key: 1,
                                modelValue: row[field.value],
                                "onUpdate:modelValue": ($event: any) => ((row[field.value]) = $event)
                              }, { ref_for: true }, getFieldProps(field, row, index), {
                                rules: field.required ? [(v) => !!v || 'Requerido'] : [],
                                variant: "solo-filled"
                              }), null, 16, ["modelValue", "onUpdate:modelValue", "rules"]))
                        ]),
                        _: 2
                      }, 1032, ["cols"]))
                    }), 128))
                  ]),
                  _: 2
                }, 1024)
              ]),
              _: 2
            }, 1024),
            _createVNode(_component_v_col, {
              cols: "1",
              class: "pa-0"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_btn, {
                  icon: "",
                  size: "small",
                  class: "elevation-0",
                  onClick: ($event: any) => (removeRow(index))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, null, {
                      default: _withCtx(() => _cache[0] || (_cache[0] = [
                        _createTextVNode("mdi-delete")
                      ])),
                      _: 1,
                      __: [0]
                    })
                  ]),
                  _: 2
                }, 1032, ["onClick"]),
                (index + 1 === localValue.value.length)
                  ? (_openBlock(), _createBlock(_component_v_btn, {
                      key: 0,
                      icon: "",
                      size: "small",
                      class: "elevation-0",
                      onClick: addRow
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, null, {
                          default: _withCtx(() => _cache[1] || (_cache[1] = [
                            _createTextVNode("mdi-plus")
                          ])),
                          _: 1,
                          __: [1]
                        })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 2
            }, 1024)
          ]),
          _: 2
        }, 1024)
      ]))
    }), 128)),
    (__props.summary?.show)
      ? (_openBlock(), _createBlock(_component_v_divider, {
          key: 0,
          class: "mb-4"
        }))
      : _createCommentVNode("", true),
    (__props.summary?.show)
      ? (_openBlock(), _createBlock(_component_v_row, { key: 1 }, {
          default: _withCtx(() => [
            _createVNode(_component_v_col, {
              cols: "12",
              class: "text-end font-weight-bold"
            }, {
              default: _withCtx(() => [
                _createTextVNode(" Total: " + _toDisplayString(formatCurrency(getTotal.value)), 1)
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})